import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import SEOHead from '../seo/SEOHead';
import { BreadcrumbSchema } from '../seo/SchemaMarkup';
import { PAGE_SEO, SITE_URL } from '../../seoConfig';
import { projects } from '../../mockData';

const Referenzen = () => {
  const breadcrumbs = [
    { name: "Start", url: SITE_URL },
    { name: "Referenzen", url: `${SITE_URL}/referenzen` }
  ];

  return (
    <>
      <SEOHead 
        title={PAGE_SEO.referenzen.title}
        description={PAGE_SEO.referenzen.description}
        canonical={PAGE_SEO.referenzen.canonical}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-24">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('/images/project-1.jpg')" }}
          ></div>
          <div className="container mx-auto px-6 relative z-10">
            {/* Breadcrumb */}
            <nav className="text-sm mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                <li><Link to="/" className="text-gray-400 hover:text-white">Start</Link></li>
                <li className="text-gray-500">/</li>
                <li className="text-[#DC2626]">Referenzen</li>
              </ol>
            </nav>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Unsere <span className="text-[#DC2626]">Referenzen</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              Überzeugen Sie sich von unserer Arbeit. Hier finden Sie eine Auswahl unserer erfolgreich abgeschlossenen Sanierungsprojekte.
            </p>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <div 
                  key={project.id}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500"
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={project.image}
                      alt={`${project.title} - Sanierungsprojekt von HK Altbausanierung`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="inline-flex items-center gap-1 text-white/80 text-sm">
                        <Calendar className="w-4 h-4" /> {project.year}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                      {project.title}
                    </h2>
                    <p className="text-gray-600 mb-4">
                      {project.description}
                    </p>
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <MapPin className="w-4 h-4 text-[#DC2626]" />
                      <span>Stuttgart & Region</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#DC2626] mb-2">13+</div>
                <div className="text-gray-600">Jahre Erfahrung</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#DC2626] mb-2">500+</div>
                <div className="text-gray-600">Projekte</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#DC2626] mb-2">100%</div>
                <div className="text-gray-600">Kundenzufriedenheit</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#DC2626] mb-2">6</div>
                <div className="text-gray-600">Fachkräfte</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#DC2626]">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ihr Projekt könnte das nächste sein
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Kontaktieren Sie uns für eine unverbindliche Besichtigung und Beratung.
            </p>
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-2 bg-white text-[#DC2626] px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors shadow-xl"
            >
              Jetzt anfragen <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Referenzen;
